import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  ThemeIcon,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { Alert, Badge, Button, Card } from "@xplatform/design-system";
import { IconAlertCircle, IconArrowLeft, IconRefresh } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";
import { reviewsApi } from "@services/reviewsApi";
import type { ReviewDownload, ReviewsListResponse } from "@models/reviews";
import { ReviewFilesList } from "./ReviewFilesList";
import { ReviewFileDetail } from "./ReviewFileDetail";
import { CorrectVerdicts } from "./CorrectVerdicts";

/**
 * Reviews page (PBI #781340). Routes, relative to the module's `reviews/*` route:
 *   ""              → KPI selection
 *   ":kpi"          → tab "Review files": one list of review files for the KPI
 *   ":kpi/correct"  → tab "Correct verdicts" (PBI #747204)
 *   ":kpi/:period"  → per-file detail view (linkable)
 * The list is loaded once here and shared by all three, so navigating between them does
 * not refetch it.
 */
export const ReviewsPage: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<ReviewsListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await reviewsApi.list());
    } catch (e) {
      setError(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Container size="xl" py="xl">
      <Group justify="space-between" mb="xl">
        <Box>
          <Title order={2}>{t("reviews.title")}</Title>
          <Text c="dimmed" size="sm" mt={4}>
            {t("reviews.subtitle")}
          </Text>
        </Box>
        <Button
          variant="subtle"
          leftSection={<IconRefresh size={16} />}
          loading={loading && data !== null}
          onClick={() => void load()}
        >
          {t("common.retry")}
        </Button>
      </Group>

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          mb="lg"
          withCloseButton
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {data === null ? (
        loading && (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        )
      ) : (
        <Routes>
          <Route index element={<KpiSelection data={data} />} />
          <Route path=":kpi" element={<KpiFiles data={data} />} />
          <Route path=":kpi/correct" element={<KpiCorrect />} />
          <Route path=":kpi/:period" element={<FileDetail data={data} onChanged={load} />} />
        </Routes>
      )}
    </Container>
  );
};

// ── Step 1: KPI selection ───────────────────────────────────────────────────

const KpiSelection: React.FC<{ data: ReviewsListResponse }> = ({ data }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const kpis = useMemo(() => [...new Set(data.downloads.map((d) => d.kpi))].sort(), [data]);

  return (
    <Stack gap="lg">
      <Box>
        <Title order={3} mb={4}>
          {t("reviews.selectKpi")}
        </Title>
        <Text c="dimmed" size="sm">
          {t("reviews.selectKpiHint")}
        </Text>
      </Box>

      {kpis.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          {t("reviews.noData")}
        </Text>
      ) : (
        <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="md">
          {kpis.map((kpi) => {
            const files = data.downloads.filter((d) => d.kpi === kpi);
            // Same definition as the list badge: only files still waiting for the reviewer.
            const outstanding = files.filter((d) => d.review_status === "outstanding").length;
            return (
              <UnstyledButton key={kpi} onClick={() => navigate(encodeURIComponent(kpi))}>
                <Card withBorder shadow="sm" radius="md" p="lg" style={{ cursor: "pointer" }}>
                  <Stack gap="xs" align="center">
                    <ThemeIcon size="xl" radius="xl" variant="light" color="blue">
                      <Text fw={700} size="sm">
                        {kpi}
                      </Text>
                    </ThemeIcon>
                    <Text fw={600} size="md">
                      {kpi}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {t("reviews.kpiCard.files", { count: files.length })}
                    </Text>
                    {outstanding > 0 && (
                      <Badge color="blue" size="xs" variant="light">
                        {t("reviews.status.outstandingCount", { count: outstanding })}
                      </Badge>
                    )}
                  </Stack>
                </Card>
              </UnstyledButton>
            );
          })}
        </SimpleGrid>
      )}
    </Stack>
  );
};

// ── Breadcrumb shared by list and detail ────────────────────────────────────

/** `toKpiSelection` is the relative path back to KPI selection from the current route. */
const KpiBreadcrumb: React.FC<{ kpi: string; toKpiSelection: string }> = ({ kpi, toKpiSelection }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Group gap="xs" mb="lg">
      <Button
        variant="subtle"
        size="xs"
        leftSection={<IconArrowLeft size={14} />}
        onClick={() => navigate(toKpiSelection, { relative: "path" })}
      >
        {t("reviews.selectKpi")}
      </Button>
      <Text c="dimmed" size="xs">
        /
      </Text>
      <Badge variant="light" size="lg">
        {kpi}
      </Badge>
    </Group>
  );
};

function filesForKpi(data: ReviewsListResponse, kpi: string): ReviewDownload[] {
  // Newest period first
  return data.downloads
    .filter((d) => d.kpi === kpi)
    .sort((a, b) => b.period.localeCompare(a.period));
}

// ── Step 2: one list per KPI ────────────────────────────────────────────────

const KpiFiles: React.FC<{ data: ReviewsListResponse }> = ({ data }) => {
  const { kpi = "" } = useParams();
  const navigate = useNavigate();
  const files = useMemo(() => filesForKpi(data, kpi), [data, kpi]);
  // PBI #936072: verdicts an imported upload could not apply, per review file.
  const droppedByKey = useMemo(() => {
    const out: Record<string, number> = {};
    for (const u of data.uploads) {
      if (u.status === "imported" && (u.failed_rows_count ?? 0) > 0) {
        out[u.original_xlsx_key] = (out[u.original_xlsx_key] ?? 0) + (u.failed_rows_count as number);
      }
    }
    return out;
  }, [data]);

  return (
    <>
      <KpiBreadcrumb kpi={kpi} toKpiSelection=".." />
      <KpiTabs active="files" />
      <ReviewFilesList
        downloads={files}
        droppedByKey={droppedByKey}
        onOpen={(d) => navigate(encodeURIComponent(d.period), { relative: "path" })}
      />
    </>
  );
};

// ── Tabs on the KPI page (PBI #747204) ──────────────────────────────────────

const KpiTabs: React.FC<{ active: "files" | "correct" }> = ({ active }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Tabs
      value={active}
      mb="md"
      onChange={(v) => {
        if (v === active) return;
        navigate(v === "correct" ? "correct" : "..", { relative: "path" });
      }}
    >
      <Tabs.List>
        <Tabs.Tab value="files">{t("reviews.tabs.files")}</Tabs.Tab>
        <Tabs.Tab value="correct">{t("reviews.tabs.correct")}</Tabs.Tab>
      </Tabs.List>
    </Tabs>
  );
};

const KpiCorrect: React.FC = () => {
  const { kpi = "" } = useParams();
  return (
    <>
      <KpiBreadcrumb kpi={kpi} toKpiSelection="../.." />
      <KpiTabs active="correct" />
      <CorrectVerdicts kpi={kpi} />
    </>
  );
};

// ── Step 3: per-file detail ─────────────────────────────────────────────────

const FileDetail: React.FC<{ data: ReviewsListResponse; onChanged: () => void }> = ({
  data,
  onChanged,
}) => {
  const { t } = useTranslation();
  const { kpi = "", period = "" } = useParams();
  const navigate = useNavigate();
  const download = data.downloads.find((d) => d.kpi === kpi && d.period === period);
  const back = () => navigate("..", { relative: "path" });

  return (
    <>
      <KpiBreadcrumb kpi={kpi} toKpiSelection="../.." />
      {download ? (
        <ReviewFileDetail download={download} onBack={back} onChanged={onChanged} />
      ) : (
        <Alert icon={<IconAlertCircle size={16} />} color="orange">
          {t("reviews.detail.notFound", { period })}
        </Alert>
      )}
    </>
  );
};
